import React, { useState, useEffect } from "react";
import {
  Shield,
  CheckCircle,
  Clock,
  FileText,
  Award,
  ArrowRight,
  Zap,
  Users,
  Lock,
  TrendingUp,
  Phone,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import PopupButton from "../components/PopupButton";
import ContactForm from "../components/ContactForm";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { pricingPlans } from "../data/mock";

const LandingPage = () => {
  const [currentProcess, setCurrentProcess] = useState(0);

  const processSteps = [
    {
      step: 1,
      title: "Submit Your Details",
      description:
        "Fill out a quick form with your brand name and business information. Our team will review your submission within 24 hours.",
      icon: FileText,
      details: [
        "Brand name verification",
        "Business information collection",
        "Initial consultation",
      ],
    },
    {
      step: 2,
      title: "We Handle the Filing",
      description:
        "Our trademark attorneys conduct comprehensive search, prepare and file your application with the USPTO using the correct classification.",
      icon: Shield,
      details: [
        "USPTO database search",
        "Professional application prep",
        "Complete documentation handling",
      ],
    },
    {
      step: 3,
      title: "Get Official Protection",
      description:
        "Once approved, your trademark is officially registered and protected nationwide with full legal rights.",
      icon: CheckCircle,
      details: [
        "Registration certificate",
        "Nationwide legal protection",
        "Ongoing monitoring support",
      ],
    },
  ];

  // Auto-slide process
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentProcess((prev) => (prev + 1) % processSteps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const benefits = [
    {
      icon: Lock,
      title: "Exclusive Rights",
      description: "Secure nationwide protection for your brand name and logo",
    },
    {
      icon: Shield,
      title: "Legal Protection",
      description:
        "Sue infringers and stop competitors from copying your brand",
    },
    {
      icon: TrendingUp,
      title: "Increase Value",
      description:
        "Registered trademarks add significant value to your business",
    },
    {
      icon: Award,
      title: "Use ® Symbol",
      description: "Display the registered trademark symbol to deter copycats",
    },
    {
      icon: Users,
      title: "Build Trust",
      description: "Show customers your brand is legitimate and protected",
    },
    {
      icon: Zap,
      title: "Fast Processing",
      description: "We file your application within 24 hours of submission",
    },
  ];

  const whyChooseUs = [
    { number: "10,000+", label: "Trademarks Filed", icon: Award },
    { number: "98%", label: "Success Rate", icon: TrendingUp },
    { number: "24hr", label: "Processing Time", icon: Clock },
    { number: "50+", label: "States Covered", icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Simplified Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/lp" className="flex items-center space-x-2">
              <img
                alt="Trademark Claim Experts"
                className="h-12 w-auto"
                src="/assets/img/logo.png"
              />
            </Link>
            <div className="flex gap-3">
              <PopupButton className="bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white px-6">
                <Phone className="w-4 h-4 mr-2" />
                Register My Trademark
              </PopupButton>
              <button className="js-chat border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 rounded-md font-medium transition-colors flex items-center">
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-brand-purple/5 via-white to-brand-pink/5 py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-200 rounded-full filter blur-3xl opacity-20 animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-gradient-to-r from-brand-purple to-brand-pink text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  ⚡ Limited Time Offer: Starting at $45
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Protect Your Brand with
                <span className="block bg-gradient-to-r from-brand-purple to-brand-pink bg-clip-text text-transparent">
                  Trademark Registration
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                <span className="font-bold text-gray-900">
                  Secure Your Brand. Stop Copycats.
                </span>{" "}
                Get full legal protection for your business name, logo, and
                identity in all 50 states.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-700">
                    98% Success Rate • 24-Hour Filing • No Hidden Fees
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-700">
                    Expert Attorneys • Full USPTO Compliance • Money-Back
                    Guarantee
                  </span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-purple to-brand-pink rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
              <div className="relative bg-white p-8 md:p-10 rounded-3xl shadow-2xl border-2 border-blue-100">
                <div className="mb-6 text-center">
                  <h3 className="text-3xl font-bold bg-gradient-to-r from-brand-purple to-brand-pink bg-clip-text text-transparent mb-3">
                    Start Your Registration Now
                  </h3>
                  <p className="text-gray-600 text-lg">
                    <span className="font-bold text-blue-600">Only $45!</span>{" "}
                    Complete the form below and our team will contact you within
                    24 hours.
                  </p>
                </div>
                <ContactForm variant="compact" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-brand-purple to-brand-pink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {whyChooseUs.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center text-white">
                  <IconComponent className="w-10 h-10 mx-auto mb-3 opacity-80 animate-float" />
                  <div className="text-4xl md:text-5xl font-bold mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm md:text-base opacity-90">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section with Animated Icons - 3 Steps */}
      <section className="py-20 bg-gradient-to-br from-brand-purple/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Our Trademark Process Works
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Simple, fast, and stress-free registration
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {processSteps.map((step) => {
              const IconComponent = step.icon;
              return (
                <Card
                  key={step.step}
                  className="relative border-2 border-blue-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-500 group overflow-hidden bg-white hover:bg-gradient-to-br hover:from-brand-purple/5 hover:to-white transform hover:-translate-y-2"
                >
                  {/* Hover Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-purple to-brand-pink opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>

                  <CardContent className="p-8 relative z-10">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full mb-6 animate-float group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 shadow-lg group-hover:shadow-2xl">
                        <IconComponent className="w-12 h-12 text-white" />
                      </div>
                      <div className="text-5xl font-bold text-blue-600 mb-4 group-hover:text-cyan-500 transition-colors duration-300">
                        Step {step.step}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    <div className="border-t border-blue-100 pt-6">
                      <ul className="space-y-3">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5 animate-pulse-grow group-hover:scale-125 transition-transform duration-300" />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors duration-300">
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>

                  {/* Step Number Badge */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl group-hover:bg-cyan-500 group-hover:scale-110 transition-all duration-300">
                    {step.step}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why You Need Trademark Registration
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Protect your brand and secure your business future
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card
                  key={index}
                  className="border-2 border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all duration-300 group"
                >
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mb-4 animate-pulse-grow group-hover:scale-110 transition-transform">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-brand-purple/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Get Started with Budget-Friendly Trademark Solutions
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative flex flex-col h-full hover:shadow-2xl transition-all duration-300 ${plan.recommended ? "ring-2 ring-blue-600 scale-105" : ""}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-brand-purple to-brand-pink text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    +Federal Filing Fee
                  </p>
                  <p className="text-sm text-gray-600 mt-4">
                    {plan.description}
                  </p>
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-64 pb-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <div className="p-6 pt-4 mt-auto">
                  <PopupButton className="w-full bg-gradient-to-r from-brand-purple to-brand-pink hover:opacity-90 text-white">
                    Register Your Trademark
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </PopupButton>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Thousands of Businesses Trust Us
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              We go beyond just filing — we're your partner in brand protection
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Benefit 1 */}
            <Card className="border-2 border-blue-100 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform animate-pulse-grow">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  100% Money-Back Guarantee
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  If we can't file your trademark, you get a full refund. No
                  questions asked. We're confident in our 98% success rate.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 2 */}
            <Card className="border-2 border-blue-100 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform animate-pulse-grow">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Expert Legal Team
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our experienced trademark attorneys handle everything from
                  comprehensive searches to USPTO filings with precision.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 3 */}
            <Card className="border-2 border-blue-100 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform animate-pulse-grow">
                  <Clock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  24-Hour Filing Promise
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We file your trademark application within 24 hours of
                  receiving your information. Fast, efficient, and reliable.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 4 */}
            <Card className="border-2 border-blue-100 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform animate-pulse-grow">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  10,000+ Trademarks Filed
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Join thousands of satisfied clients who have successfully
                  protected their brands with our proven process.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 5 */}
            <Card className="border-2 border-blue-100 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform animate-pulse-grow">
                  <MessageCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  24/7 Customer Support
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Have questions? Our dedicated support team is available around
                  the clock to assist you at every step.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 6 */}
            <Card className="border-2 border-blue-100 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 group">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-brand-purple to-brand-pink rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform animate-pulse-grow">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Transparent Pricing
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  No hidden fees, no surprises. What you see is what you pay.
                  Simple, honest, and budget-friendly pricing.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Additional Trust Section */}
          <div className="bg-gradient-to-r from-brand-purple to-brand-pink rounded-3xl p-12 text-white text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Secure Your Brand Identity?
            </h3>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Don't wait until someone copies your brand. Protect your business
              name, logo, and identity today with our trusted trademark
              registration services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <PopupButton
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                <Phone className="w-5 h-5 mr-2" />
                Start Your Registration Now
              </PopupButton>
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">
                  Only $45 • No Hidden Fees • 100% Secure
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-brand-purple to-brand-pink text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Join 10,000+ businesses who trust us with their trademark
            registration. Start today for only $45!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PopupButton
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              <Phone className="w-5 h-5 mr-2" />
              Register My Trademark
            </PopupButton>
            <button className="js-chat border-2 border-white text-white hover:bg-white hover:text-blue-600 shadow-xl px-8 py-1 rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Chat Now
            </button>
          </div>
          <p className="text-sm mt-6 opacity-75">
            No hidden fees • Money-back guarantee • 24/7 support
          </p>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="py-6 bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Trademark Claim Experts. All Rights
              Reserved
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Link
                to="/privacy-policy"
                className="hover:text-blue-600 transition-colors"
              >
                Privacy Policy
              </Link>
              <span>|</span>
              <Link
                to="/terms-conditions"
                className="hover:text-blue-600 transition-colors"
              >
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
